import { ExperiencePerformanceTypes } from '@atlassian/ufo-experimental';
import {
	type CustomHistogramConfig,
	type ExperienceData,
	FMP_MARK,
	type HistogramConfig,
	INLINE_RESPONSE_MARK,
	type InteractionHistogramConfig,
	type PageLoadExperienceData,
	type PageLoadHistogramConfig,
	type PageSegmentLoadHistogramConfig,
	type ExperiencePerformanceConfig as PerformanceConfig,
} from '@atlassian/ufo-experimental/types';

import { type BundleEvalTimingsConfig, type ResourceTimingsConfig } from '../../../types';
import { type SSRConfig } from '../../types';
import { isInitialPageLoad } from '../utils/is-initial-page-load';

import { navigationMetrics } from './plugins/navigation-metrics';
import { appTimings } from './plugins/timings/app';
import { bundleEvalTimings } from './plugins/timings/bundle-eval';
import { resourceTimings } from './plugins/timings/resource';

const LoadMetricKeys = {
	tti: 'metric:tti',
	fmp: 'metric:fmp',
	ttiHistograms: 'metric:tti:histogramBuckets',
	fmpHistograms: 'metric:fmp:histogramBuckets',
};

const InlineResultMetricKeys = {
	result: 'metric:result',
	response: 'metric:response',
	resultHistograms: 'metric:result:histogramBuckets',
	responseHistograms: 'metric:response:histogramBuckets',
};

const CustomMetricKeys = {
	duration: 'metric:duration',
	durationHistograms: 'metric:duration:histogramBuckets',
};

const getHistogram = (
	data: ExperienceData | PageLoadExperienceData,
	performanceConfig: PerformanceConfig | null,
	isInitial?: boolean,
) => {
	let histogram: HistogramConfig = {};
	if (data.performanceConfig?.histogram) {
		histogram = data.performanceConfig.histogram;
	} else if (performanceConfig?.histogram) {
		histogram = performanceConfig.histogram;
	}

	if (histogram) {
		const histograms = histogram[data.performanceType];
		switch (data.performanceType) {
			case ExperiencePerformanceTypes.PageLoad:
			case ExperiencePerformanceTypes.PageSegmentLoad:
				const histogramType =
					isInitial !== undefined && isInitial === true ? 'initial' : 'transition';
				return histograms
					? {
							[LoadMetricKeys.ttiHistograms]: (
								histograms as PageLoadHistogramConfig | PageSegmentLoadHistogramConfig
							)[histogramType].tti,
							[LoadMetricKeys.fmpHistograms]: (
								histograms as PageLoadHistogramConfig | PageSegmentLoadHistogramConfig
							)[histogramType].fmp,
						}
					: {};
			case ExperiencePerformanceTypes.InlineResult:
				return histograms
					? {
							[InlineResultMetricKeys.resultHistograms]: (histograms as InteractionHistogramConfig)
								.result,
							[InlineResultMetricKeys.responseHistograms]: (
								histograms as InteractionHistogramConfig
							).response,
						}
					: {};
			case ExperiencePerformanceTypes.Custom:
				return histograms
					? {
							[CustomMetricKeys.durationHistograms]: (histograms as CustomHistogramConfig).duration,
						}
					: {};
		}
	}
};

export const getLoadMetrics = (
	data: ExperienceData | PageLoadExperienceData,
	ssr: SSRConfig | null,
	performanceConfig: PerformanceConfig | null,
) => {
	if (data.metrics.endTime === null || data.metrics.startTime === null) {
		return null;
	}

	const fmpMark =
		data.metrics.marks.find((mark) => mark.name === FMP_MARK)?.time ||
		(isInitialPageLoad(data) && ssr?.getDoneMark());
	const tti = data.metrics.endTime - data.metrics.startTime;
	const fmp = typeof fmpMark === 'number' ? fmpMark - data.metrics.startTime : tti;
	return {
		[LoadMetricKeys.tti]: Math.round(tti),
		[LoadMetricKeys.fmp]: Math.round(fmp),
		...getHistogram(data, performanceConfig),
	};
};

export const getInlineResultMetrics = (
	data: ExperienceData,
	performanceConfig: PerformanceConfig | null,
) => {
	if (data.metrics.endTime === null || data.metrics.startTime === null) {
		return null;
	}

	const responseMark = data.metrics.marks.find((mark) => mark.name === INLINE_RESPONSE_MARK);
	const result = data.metrics.endTime - data.metrics.startTime;
	const response = responseMark ? responseMark.time - data.metrics.startTime : result;
	return {
		[InlineResultMetricKeys.result]: Math.round(result),
		[InlineResultMetricKeys.response]: Math.round(response),
		...getHistogram(data, performanceConfig),
	};
};

export const getCustomMetrics = (
	data: ExperienceData,
	performanceConfig: PerformanceConfig | null,
) => {
	if (data.metrics.endTime === null || data.metrics.startTime === null) {
		return null;
	}

	const duration = data.metrics.endTime - data.metrics.startTime;
	return {
		[CustomMetricKeys.duration]: Math.round(duration),
		...getHistogram(data, performanceConfig),
	};
};

const getPerformanceMetrics = (
	data: ExperienceData | PageLoadExperienceData,
	ssr: SSRConfig | null,
	performanceConfig: PerformanceConfig | null,
) => {
	switch (data.performanceType) {
		case ExperiencePerformanceTypes.PageLoad:
		case ExperiencePerformanceTypes.PageSegmentLoad:
			return getLoadMetrics(data, ssr, performanceConfig);
		case ExperiencePerformanceTypes.InlineResult:
			return getInlineResultMetrics(data, performanceConfig);
		case ExperiencePerformanceTypes.Custom:
			return getCustomMetrics(data, performanceConfig);
	}
};

export const getPerformancePayload = (
	data: ExperienceData | PageLoadExperienceData,
	ssr: SSRConfig | null,
	bundleEvalConfig: BundleEvalTimingsConfig | null,
	resourceConfig: ResourceTimingsConfig | null,
	performanceConfig: PerformanceConfig | null,
) => {
	return {
		...getPerformanceMetrics(data, ssr, performanceConfig),
		...resourceTimings(data, resourceConfig),
		...bundleEvalTimings(data, bundleEvalConfig),
		...appTimings(data, ssr),
		...(isInitialPageLoad(data) ? navigationMetrics() : null),
	};
};
